<template>
    <v-container fluid>
        <no-data-dialog v-if="notifyDialog" @input="notifyDialog = false" />
        <v-row v-if="!isAudit || showButtons">
            <v-col>
                <v-text-field v-model="search" append-icon="mdi-search" label="Search" single-line hide-details />
            </v-col>
            <v-col>
                <v-btn color="quaternary" dark class="ml-2 mb-2 float-right" v-on:click="download()">Export to Excel</v-btn>
                <v-dialog v-model="dialog" max-width="500px" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
                    <template v-slot:activator="{ on }">
                        <v-btn v-if="canEdit(claimName) && !isAddDisabled" color="primary" dark v-on="on" class="ml-2 mb-2 float-right" @click="addItem">Add {{ formTitle }}</v-btn>
                    </template>

                    <component :is="tableForm" :filter="tableFilter" v-model="editedItem" @cancel="dialog = false" @open="dialog = true" @input="onInput()" @delete="onDelete" :key="formKey" />
                </v-dialog>
                <v-file-input @change="importExcelFile" ref="file" style="display: none" :key="'fileKey' + fileKey" />
                <v-btn v-if="showExcelImportButtons" color="quinary" dark class="ml-2 mb-2 float-right" @click="$refs.file.$refs.input.click()">Import from Excel</v-btn>
                <v-btn v-if="showExcelImportButtons" color="tertiary" dark class="ml-2 mb-2 float-right" @click="downloadTemplate()">Download Template</v-btn>
            </v-col>
        </v-row>
        <slot name="tabs" />
        <v-row>
            <v-col>
                <v-data-table :class="{ 'edit-data-table': hasEditPage && !showExpand && computedItems.length > 0, 'elevation-1': true }" :height="getTableHeight" :items-per-page="getItemsPerPage" :footer-props="footerProps" :headers="filteredHeaders" :items="computedItems" :options.sync="options" :server-items-length="total" :custom-sort="customSort" :loading="loading" item-key="id" :show-expand="showExpand" single-expand @click:row="goToEditPage" fixed-header>
                    <template v-for="header in headersLocal" v-slot:[getSlotName(header.value)]="{ item }">
                        <v-menu v-if="header.form && header.table === 'files' && item[header.value] !== null" open-on-hover offset-y :key="header.title" max-width="300">
                            <template v-slot:activator="{ on }">
                                <v-chip class="hover-cell" v-on="on" @click.stop.prevent="downloadFile(item, header)">
                                    <table-item-display :header="header" :item="item" :api-data="apiData" />
                                    <v-icon>mdi-download</v-icon>
                                </v-chip>
                            </template>
                            <v-card>
                                <component :is="image" :value="item[header.value]" :filter="tableFilter" :is-hover="true" />
                            </v-card>
                        </v-menu>
                        <v-menu v-else-if="header.form" open-on-hover offset-y :key="header.title" max-width="300">
                            <template v-slot:activator="{ on }">
                                <div class="hover-cell" v-on="on">
                                    <table-item-display :header="header" :item="item" :api-data="apiData" />
                                </div>
                            </template>
                            <v-card @click="goToTable(header.table, item[header.value])" class="edit-data-table">
                                <component :is="header.form" :value="item[header.value]" :filter="tableFilter" :is-hover="true" />
                            </v-card>
                        </v-menu>
                        <v-menu v-else-if="header.format && header.format.name == formatCoordinates.name" open-on-hover offset-y :key="header.title" max-width="300" :close-on-content-click="false">
                            <template v-slot:activator="{ on }">
                                <div class="hover-cell" v-on="on">
                                    {{ header.format(item[header.value]) }}
                                </div>
                            </template>
                            <v-card>
                                <map-hover-tile :value="item[header.value]" />
                            </v-card>
                        </v-menu>
                        <template v-else>
                            <table-item-display :header="header" :item="item" :api-data="apiData" :key="header.title" />
                        </template>
                    </template>
                    <template v-if="canRead(claimName)" v-slot:[`item.action`]="{ item }">
                        <div class="d-flex flex-nowrap justify-end">
                            <v-icon v-if="canEdit(claimName) && !isEditDisabled" class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
                            <slot name="buttons" :item="item" />
                            <v-icon v-if="hasEditPage" @click="goToEditPage(item)">mdi-arrow-right-thick</v-icon>
                        </div>
                    </template>
                    <template v-slot:expanded-item="{ item }">
                        <td class="pa-2" :colspan="filteredHeaders.length+1">
                            <slot name="expand" :item="item" :refresh-key="refreshKey" />
                        </td>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from 'axios'
    import exporter from '../export/exporter'
    import store from '../store/store'
    import errorUtility from '../services/errorUtility'

    export default {
        props: [
            'filter',
            'headers',
            'apiData',
            'sorting',
            'itemsField',
            'editPageName',
            'apiMethod',
            'claimName',
            'formTitle',
            'excelTitle',
            'tableForm',
            'showExpand',
            'refreshKey',
            'setApiData',
            'hasEditPage',
            'isNotServerHandled',
            'isAddDisabled',
            'isEditDisabled',
            'hasImport',
            'hasMemberImport',
            'showButtons'
        ],

        components: {
            noDataDialog: () => import('../export/NoDataDialog'),
            mapHoverTile: () => import('../components/MapHoverTile'),
            tableItemDisplay: () => import('../components/TableItemDisplay')
        },

        data() {
            return {
                loading: true,
                search: '',
                total: undefined,
                options: {},

                image: () => import('../components/FileImage'),
                dialog: false,
                notifyDialog: false,
                editedItem: {},
                formKey: 0,
                fileKey: 0,
                previousFilter: {},
                footerProps: { 'items-per-page-options': [15, 30, 50, 100] },
            }
        },

        mounted() {
            this.previousFilter = this.filter;
            this.dialog = false;
            for (var i = 0; i < this.headersLocal.length; i++) {
                this.headersLocal[i].class = 'grey lighten-3';
            }
        },

        methods: {
            async load() {
                try {
                    this.loading = true;
                    var params = {};
                    var storeFilter = this.tableFilter;
                    if (typeof this.tableFilter !== 'undefined' && this.tableFilter !== null) {
                        Object.keys(this.tableFilter).forEach(key => params['filter.' + key] = this.tableFilter[key]);
                    }
                    if (this.sorting !== null && typeof this.sorting !== "undefined") {
                        for (var i = 0; i < this.sorting.length; i++) {
                            if (!this.tableFilter.hasOwnProperty(this.sorting[i])) {
                                params.sortBy = this.sorting[i].column;
                                params.direction = this.sorting[i].direction;
                                break;
                            }
                        }
                    }
                    if (!this.isNotServerHandled) {
                        params.skip = (this.options.page - 1) * this.options.itemsPerPage;
                        params.take = this.options.itemsPerPage;
                        params.search = this.search;
                        if (this.options.sortBy.length > 0) {
                            params.sortBy = this.options.sortBy[0];
                            if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
                                params.direction = "Descending";
                            }
                        }
                    }
                    var response = await axios.get('/api/' + this.apiMethod, { params });
                    if (JSON.stringify(storeFilter) !== JSON.stringify(this.tableFilter)) {
                        return;
                    }
                    var data = response.data;
                    if (!this.isNotServerHandled) {
                        this.total = data.total;
                    }
                    if (!this.setApiData) {
                        for (var key in this.apiData) {
                            this.$set(this.apiData, key, data[key]);
                        }
                    }
                    else {
                        this.setApiData(data);
                    }
                } catch (error) {
                    errorUtility.handleServerError(error);
                } finally {
                    this.loading = false;
                }
            },

            customSort(items, index, isDesc) {
                if (index.length === 0) {
                    return items;
                }
                var header = this.headersLocal.filter(i => i.value === index[0])[0];
                var tableName = header.hasOwnProperty('table') ? header.table : null;
                if (tableName === null) {
                    items.sort((a, b) => {
                        if (!isDesc[0]) {
                            return (a[index[0]] < b[index[0]]) ? -1 : 1;
                        } else {
                            return (b[index[0]] < a[index[0]]) ? -1 : 1;
                        }
                    });
                    return items;
                }
                items.sort((a, b) => {
                    if (!isDesc[0]) {
                        return this.apiData[tableName][a[index[0]]].localeCompare(this.apiData[tableName][b[index[0]]], undefined, { numeric: true });
                    }
                    else {
                        return this.apiData[tableName][b[index[0]]].localeCompare(this.apiData[tableName][a[index[0]]], undefined, { numeric: true });
                    }
                })
                return items;
            },

            async importExcelFile(excelFile) {
                try {
                    var formData = new FormData();
                    formData.append("file", excelFile);
                    this.loading = true;
                    await axios.post('/api/' + this.apiMethod + '/Import', formData);
                    store.dispatch('alert/success', 'Records imported successfully')
                    await this.load();
                }
                catch (error) {
                    if (error.response && error.response.status == 400) {
                        store.dispatch('alert/error', "Server returned an error.\n" + error.response.data);
                    } else {
                        errorUtility.handleServerError(error);
                    }
                }
                finally {
                    this.loading = false;
                    this.fileKey++;
                }
            },

            download() {
                if (!this.computedItems || this.computedItems.length === 0) {
                    this.notifyDialog = true;
                    return;
                }
                var self = this;
                var items = this.computedItems.map(i => {
                    var row = {};
                    this.headersLocal
                        .filter(e => e.text !== '')
                        .forEach(e => {
                            if (!e.hasOwnProperty('table')) {
                                row[e.text] = i[e.value];
                            }
                            else {
                                row[e.text] = self.apiData[e.table][i[e.value]];
                            }
                        });
                    return row;
                });

                exporter.downloadAsExcel({ title: this.excelTitle, items });
            },

            async downloadFile(item, header) {
                if (header.table !== 'files') {
                    return;
                }
                this.downloadUrl('/api/Files/GetFile/' + item[header.value], this.apiData[header.table][item[header.value]])
            },

            onInput() {
                this.dialog = false;
                this.load();
            },

            downloadTemplate() {
                return axios
                    .get('/api/' + this.apiMethod + '/Template', { responseType: 'blob' })
                    .then(exporter.saveBlob)
                    .catch((error) => { store.dispatch('alert/error', 'There was an error downloading the file. ' + error) });
            },

            downloadUrl(url, name) {
                fetch(url)
                    .then(resp => resp.blob())
                    .then(blob => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        // the filename you want
                        a.download = name;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                    })
                    .catch(() => alert('oh no!'));
            },

            getSlotName(value) {
                return "item." + value;
            },

            addItem() {
                this.formKey++;
                this.editedItem = null;
                this.dialog = true;
            },

            editItem(item) {
                this.formKey++;
                this.editedItem = item;
                this.dialog = true;
            },

            async goToEditPage(item) {
                if (this.hasEditPage && !this.showExpand && !this.dialog) {
                    this.$router.push({
                        name: this.editPageName,
                        params: {
                            id: item.id
                        },
                        query: {
                            filter: this.filter
                        }
                    });
                }
            },

            async goToTable(tableName, id) {
                this.$router.push({
                    name: tableName,
                    params: {},
                    query: {
                        filter: { ...this.filter, id }
                    }
                });
            },

            onDelete(item) {
                var items = this.apiData.items;
                var index = items.findIndex(i => i.id === item.id);

                if (index > -1) {
                    this.$set(this.apiData, items, items.splice(index, 1));
                }
                this.dialog = false;
            },

            itemMatches(item, tokens) {
                for (var i = 0; i < tokens.length; i++) {
                    var token = tokens[i];
                    var isMatch = false;
                    var self = this;
                    this.headersLocal
                        .filter(e => e.text !== '')
                        .forEach(e => {
                            if (!e.hasOwnProperty('table') && self.includes(item[e.value], token)
                                || e.hasOwnProperty('table') && self.includes(self.apiData[e.table][item[e.value]], token)) {
                                isMatch = true;
                            }
                        });

                    if (!isMatch) {
                        return false;
                    }
                }
                return true;
            },

            includes(value, token) {
                if (!value) {
                    return false;
                }
                return ('' + value).toLowerCase().includes(token);
            }
        },

        computed: {
            filteredHeaders() {
                if (typeof this.tableFilter === 'undefined' || this.tableFilter === null) {
                    return this.headersLocal;
                }

                return this.headersLocal.filter(h => !this.tableFilter.hasOwnProperty(h.value));
            },

            computedItems() {
                if (!this.isNotServerHandled) {
                    return this.apiData.items;
                }
                var tokens = this.search.toLowerCase().trim().split(' ');
                if (tokens.length === 1 && tokens[0] === "") {
                    return this.apiData.items;
                }
                return this.apiData.items.filter(i => this.itemMatches(i, tokens));
            },
            tableFilter() {
                return this.getFilter(this.filter);
            },
            isAudit() {
                return this.$route.name == "auditEditor";
            },
            getTableHeight() {
                if (this.$route.name === this.itemsField) {
                    const viewHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
                    return viewHeight - 360;
                }
                else {
                    return undefined;
                }
            },
            showExcelImportButtons() {
                return this.hasImport && this.hasClaim(this.claimName, true) || this.hasMemberImport && this.hasClaim('AddMembers');
            },
            getItemsPerPage() {
                if (this.$route.name === this.itemsField) {
                    var itemsPerPage = Math.floor((this.getTableHeight - 50) / 49);
                    return itemsPerPage;
                }
                else {
                    return undefined;
                }
            },
            headersLocal: {
                get() {
                    return this.headers;
                },
                set(headers) {
                    this.$emit("input", headers);
                },
            },
        },
        watch: {
            filter: {
                handler() {
                    if (JSON.stringify(this.previousFilter) !== JSON.stringify(this.filter)) {
                        this.load();
                        this.previousFilter = Object.assign({}, this.filter);
                    }
                }
            },
            refreshKey: {
                handler() {
                    this.load();
                }
            },
            options: {
                deep: true,
                handler() {
                    this.load();
                }
            },
            search() {
                if (!this.isNotServerHandled) {
                    this.load();
                }
            }
        }
    };
</script>
<style>
    .edit-data-table tbody tr {
        cursor: pointer;
    }
</style>
